/**
 * Registered commands of the note lane (W2-03 note-lane; WORKPACKAGES Appendix B: `notes.*`,
 * `edit.copy|cut|paste|pasteGhost|pasteImmediate|duplicate|delete|selectAll`, `tools.select|tap|drag|flick|hold|eraser`,
 * `lane.*`; DESIGN.md §5.3-5.4, §8.2-8.3; RPE NOTE-*, SEL-*, EXEC-*).
 *
 * Selection-dependent keys use `sel.notes && !textInput`: on a selection that mixes notes and keys these commands
 * act on both kinds (A mirrors the notes and negates the keys, time nudges move both, Left / Right move the notes
 * in X and the keys in value, S is disabled with a reason unless every selected key is an opacity key), so they are
 * a step more specific than a motion column's `sel.keys` binding of the same key. Every command has a menu, palette
 * or button path; Alt chords declare their Alt-free replacement (or none).
 *
 * Locked lines (DESIGN.md §4.10: lock only blocks editing) refuse every edit of their notes and keys: the commands
 * are disabled with the reason, and a run anyway (keys, toasts) explains it. Selecting and copying still work.
 *
 * RPE preset Ctrl+wheel with notes selected fine-tunes their width (NOTE-13, undoable): the lane's wheel handler
 * (laneGestures.ts) takes it over the lane, where a trackpad pinch can still fall through to the time zoom; the
 * command `notes.wheelWidth` does the same from the palette or a user binding.
 */

import { Beats } from '../../core/time/beat';
import type { Beat } from '../../core/time/types';
import type { Json, MessageRef } from '../../core/util/types';
import type { KeyId, LineId, NoteId, NoteKind } from '../../core/model/types';
import { Core } from '../../core/commands/index';
import { deleteSegments, planSegmentDelete } from '../../core/commands/segmentDelete';
import type { TxBuilder } from '../../core/ops/types';
import { DIALOG } from '../../app/dialogs/types';
import type { AppServices, CommandContext, CommandDef, KeyBinding, KeymapPresetId } from '../../app/registry/types';
import type { MessageKey } from '../../i18n/types';
import type { ToolId } from '../../state/types';
import { fitXView, clampXView, lineOfLaneId, ACTIVE_LANE_ID } from '../../render/timeline/laneColumn';
import { m } from './noteLane.messages';
import { fineTuneWidth, keyValueEdits, stepsOf } from './noteTune';
import {
  activeLineOf,
  hostOf,
  itemsText,
  laneSession,
  lockedReason,
  noteIndexOf,
  playheadBeat,
  ref,
  selectedItems,
  setSides,
  snapBeatAt,
  snapStepBeat,
  xNudgeStep,
  type LaneSides,
} from './laneSession';
import {
  cancelHoldPlacement,
  lanePointer,
  placeAllowed,
  placeAtMouse,
  placeAtPlayhead,
  toggleHoldPlacement,
} from './placement';
import {
  cancelGhost,
  commitGhost,
  copySelection,
  cycleGhostLine,
  duplicateSelection,
  parsePasteArgs,
  pasteRest,
  pasteSpecial,
  shiftGhost,
  startPaste,
  toggleGhostMirror,
} from './pasteFlow';

type Enabled = true | MessageRef;

// ---------------------------------------------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------------------------------------------

function projectOpen(ctx: CommandContext): Enabled {
  return ctx.store !== null ? true : ref(m.reasonNoProject);
}

function obj(args: unknown): Readonly<Record<string, unknown>> {
  return typeof args === 'object' && args !== null && !Array.isArray(args) ? (args as Record<string, unknown>) : {};
}

function numArg(args: unknown, key: string, fallback: number): number {
  if (typeof args === 'number' && Number.isFinite(args)) return args;
  const v = obj(args)[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function lineArg(ctx: CommandContext, args: unknown): LineId | null {
  const v = typeof args === 'number' ? args : obj(args).line;
  if (typeof v !== 'number' || ctx.store === null) return null;
  return ctx.store.doc.lines.has(v as LineId) ? (v as LineId) : null;
}

/**
 * Selected notes that still exist; a reason when none, or when one of them (with `alsoKeys`: or one of the selected
 * keys, which the command changes too) sits on a locked line.
 */
function editableNotes(ctx: CommandContext, alsoKeys = false): NoteId[] | MessageRef {
  const store = ctx.store;
  if (store === null) return ref(m.reasonNoProject);
  if (store.gestureActive) return ref(m.reasonGesture);
  const ids = selectedItems(ctx).notes;
  if (ids.length === 0) return ref(m.reasonNoNotes);
  return lockedReason(ctx, alsoKeys ? { notes: ids, keys: keysOfSelection(ctx) } : { notes: ids }) ?? ids;
}

function notesEnabled(ctx: CommandContext): Enabled {
  const r = editableNotes(ctx);
  return Array.isArray(r) ? true : r;
}

/** Notes and the selected keys are both changed (mirror, flip, nudges). */
function notesKeysEnabled(ctx: CommandContext): Enabled {
  const r = editableNotes(ctx, true);
  return Array.isArray(r) ? true : r;
}

/** Toasts a refusal reason (a run from a key whose command is disabled). */
function refuse(ctx: CommandContext, reason: MessageRef): void {
  ctx.ui.toast(reason, { kind: reason.key === m.reasonLocked ? 'warning' : 'info' });
}

/** Selected keys plus both keys of every selected segment (time and value operations act on them). */
export function keysOfSelection(ctx: Pick<AppServices, 'store' | 'editor'>): KeyId[] {
  const store = ctx.store;
  if (store === null) return [];
  const sel = selectedItems(ctx);
  const out = new Set<KeyId>(sel.keys);
  for (const id of sel.segments) {
    out.add(id);
    const next = store.idx.nextKey(id);
    if (next !== undefined) out.add(next);
  }
  return [...out];
}

/** Runs one note transaction with a label; toasts the failure. */
function noteTx<R>(
  ctx: CommandContext,
  label: MessageRef,
  command: string,
  build: (tx: TxBuilder) => R,
  coalesceKey?: string,
): R | null {
  const store = ctx.store;
  if (store === null) return null;
  const out = store.transact(coalesceKey === undefined ? { label, command } : { label, command, coalesceKey }, build);
  if (!out.ok) {
    ctx.ui.toast(out.reason, { kind: 'error' });
    return null;
  }
  for (const n of out.notes) ctx.ui.toast(n, { kind: 'info' });
  return out.value;
}

function selHash(ids: readonly number[]): string {
  return [...ids].sort((a, b) => a - b).join(',');
}

function isOpacityKey(ctx: CommandContext, key: KeyId): boolean {
  const store = ctx.store;
  if (store === null) return false;
  const k = store.doc.keys.get(key);
  const t = k === undefined ? undefined : store.doc.tracks.get(k.track);
  return t?.owner.kind === 'line' && t.owner.prop === 'alpha';
}

function setTool(ctx: CommandContext, tool: ToolId): void {
  const e = ctx.editor.getState();
  e.setTool(tool);
  if (tool === 'tap' || tool === 'drag' || tool === 'flick' || tool === 'hold') e.setPlaceKind(tool);
  laneSession(ctx).redraw(['overlay']);
}

const kb = (chord: string, command: string, extra: Partial<KeyBinding> = {}): KeyBinding => ({
  chord,
  command,
  ...extra,
});
const NOTES_WHEN = 'sel.notes && !textInput';
/** Module atom (noteLane.module.tsx): markers are selected. */
export const MARKERS_ATOM = 'markers.selected';
const MARKERS_WHEN = `${MARKERS_ATOM} && !sel.notes && !sel.keys && !sel.segments && !textInput`;
const LANE_MENU = 'lane.context' as const;

// ---------------------------------------------------------------------------------------------------------------
// Tools
// ---------------------------------------------------------------------------------------------------------------

function toolCommand(id: string, tool: ToolId, title: MessageKey, icon: string, hint: number): CommandDef {
  return {
    id,
    title,
    category: 'Tools',
    icon,
    aliases: [tool, `${tool} tool`],
    level: 'guided',
    hint,
    checked: (ctx) => ctx.editor.getState().tool === tool,
    menus: [{ menu: 'tools.menu', group: 'tools', order: hint }],
    run: (ctx) => {
      setTool(ctx, tool);
    },
  };
}

const toolCommands: readonly CommandDef[] = [
  toolCommand('tools.select', 'select', m.toolSelect, 'MousePointer2', 1),
  toolCommand('tools.tap', 'tap', m.toolTap, 'RectangleHorizontal', 2),
  toolCommand('tools.drag', 'drag', m.toolDrag, 'Minus', 3),
  toolCommand('tools.flick', 'flick', m.toolFlick, 'ArrowBigUp', 4),
  toolCommand('tools.hold', 'hold', m.toolHold, 'RectangleVertical', 5),
  toolCommand('tools.eraser', 'eraser', m.toolEraser, 'Eraser', 6),
];

// ---------------------------------------------------------------------------------------------------------------
// Placement
// ---------------------------------------------------------------------------------------------------------------

function overLaneEnabled(ctx: CommandContext): Enabled {
  if (ctx.store === null) return ref(m.reasonNoProject);
  const p = lanePointer(ctx);
  if (p === null) return ref(m.reasonNotOverLane);
  return placeAllowed(ctx, p.line);
}

function playheadEnabled(ctx: CommandContext): Enabled {
  if (ctx.store === null) return ref(m.reasonNoProject);
  return placeAllowed(ctx, activeLineOf(ctx));
}

function placeCommand(
  id: string,
  kind: Exclude<NoteKind, 'hold'>,
  title: MessageKey,
  atPlayhead: boolean,
  hint: number,
): CommandDef {
  return {
    id,
    title,
    category: 'Notes',
    icon: kind === 'tap' ? 'RectangleHorizontal' : kind === 'drag' ? 'Minus' : 'ArrowBigUp',
    aliases: atPlayhead ? [`${kind} at playhead`, 'record', 'NOTE-01'] : [`place ${kind}`, 'NOTE-01', 'NOTE-02'],
    level: 'guided',
    hint,
    enabled: atPlayhead ? playheadEnabled : overLaneEnabled,
    run: (ctx) => {
      if (atPlayhead) placeAtPlayhead(ctx, kind, id);
      else placeAtMouse(ctx, kind, id);
    },
  };
}

const placementCommands: readonly CommandDef[] = [
  placeCommand('notes.placeTap', 'tap', m.placeTap, false, 9),
  placeCommand('notes.placeDrag', 'drag', m.placeDrag, false, 8),
  placeCommand('notes.placeFlick', 'flick', m.placeFlick, false, 8),
  placeCommand('notes.placeTapAtPlayhead', 'tap', m.placeTapAtPlayhead, true, 4),
  placeCommand('notes.placeDragAtPlayhead', 'drag', m.placeDragAtPlayhead, true, 3),
  placeCommand('notes.placeFlickAtPlayhead', 'flick', m.placeFlickAtPlayhead, true, 3),
  {
    id: 'notes.hold',
    title: m.holdAtMouse,
    category: 'Notes',
    icon: 'RectangleVertical',
    aliases: ['hold', 'R R', 'NOTE-03'],
    level: 'guided',
    hint: 8,
    enabled: (ctx) => (ctx.editor.getState().placement?.kind === 'hold' ? true : overLaneEnabled(ctx)),
    run: (ctx) => {
      toggleHoldPlacement(ctx, false, 'notes.hold');
    },
  },
  {
    id: 'notes.holdAtPlayhead',
    title: m.holdAtPlayhead,
    category: 'Notes',
    icon: 'RectangleVertical',
    aliases: ['hold at playhead', 'record hold'],
    level: 'guided',
    hint: 3,
    enabled: (ctx) => (ctx.editor.getState().placement?.kind === 'hold' ? true : playheadEnabled(ctx)),
    run: (ctx) => {
      toggleHoldPlacement(ctx, true, 'notes.holdAtPlayhead');
    },
  },
  {
    id: 'notes.cancelHold',
    title: m.cancelHold,
    category: 'Notes',
    icon: 'X',
    level: 'guided',
    enabled: (ctx) => (ctx.editor.getState().placement?.kind === 'hold' ? true : ref(m.reasonNoHoldPlacing)),
    run: (ctx) => {
      cancelHoldPlacement(ctx);
    },
  },
];

// ---------------------------------------------------------------------------------------------------------------
// Note editing
// ---------------------------------------------------------------------------------------------------------------

function notesCommand(
  id: string,
  title: MessageKey,
  icon: string,
  aliases: readonly string[],
  group: string,
  order: number,
  body: (ctx: CommandContext, ids: NoteId[]) => void,
  extra: Partial<CommandDef> = {},
  alsoKeys = false,
): CommandDef {
  return {
    id,
    title,
    category: 'Notes',
    icon,
    aliases,
    level: 'guided',
    enabled: alsoKeys ? notesKeysEnabled : notesEnabled,
    menus: [{ menu: LANE_MENU, group, order }],
    run: (ctx) => {
      const ids = editableNotes(ctx, alsoKeys);
      if (!Array.isArray(ids)) {
        refuse(ctx, ids);
        return;
      }
      body(ctx, ids);
    },
    ...extra,
  };
}

function convertCommand(id: string, kind: NoteKind, title: MessageKey, order: number): CommandDef {
  return notesCommand(id, title, 'Replace', [`to ${kind}`, 'convert', 'EXEC-06'], 'convert', order, (ctx, ids) => {
    const holdLen = snapStepBeat(ctx.editor.getState().snap);
    noteTx(ctx, ref(m.txConvert, { count: ids.length, kind }), id, (tx) => {
      Core.notes.convertNotes(tx, { ids, kind, holdLen });
    });
  });
}

/** Moves the selection by `steps` snap steps in time (notes and keys together). */
export function nudgeTime(ctx: CommandContext, steps: number): void {
  const ids = editableNotes(ctx, true);
  if (!Array.isArray(ids)) {
    refuse(ctx, ids);
    return;
  }
  const store = ctx.store;
  if (store === null) return;
  const keys = keysOfSelection(ctx);
  const step = snapStepBeat(ctx.editor.getState().snap);
  let dt: Beat = Beats.mulInt(step, steps);
  // Never before beat 0: clamp to the earliest item.
  let min: Beat | null = null;
  for (const id of ids) {
    const n = store.doc.notes.get(id);
    if (n !== undefined && (min === null || Beats.lt(n.t, min))) min = n.t;
  }
  for (const id of keys) {
    const k = store.doc.keys.get(id);
    if (k !== undefined && (min === null || Beats.lt(k.t, min))) min = k.t;
  }
  if (min !== null && Beats.lt(Beats.add(min, dt), Beats.ZERO)) dt = Beats.sub(Beats.ZERO, min);
  if (Beats.eq(dt, Beats.ZERO)) return;
  const count = ids.length + keys.length;
  noteTx(
    ctx,
    ref(m.txNudge, { count }),
    'notes.nudge',
    (tx) => {
      if (keys.length > 0) Core.transform.transform(tx, { notes: ids, keys, spec: { dt } });
      else Core.notes.moveNotes(tx, { ids, dt, dx: 0, snapDiv: ctx.editor.getState().snap.div });
    },
    `nudge:${selHash([...ids, ...keys])}`,
  );
}

/**
 * Moves the selected notes by `steps` half X-grid steps. Value operations act on their kind (DESIGN.md §5.4): selected
 * keyframes (and both keys of selected segments) move by `keySteps` value steps in the same undoable step, as the
 * motion columns' Left / Right do on keys alone.
 */
export function nudgeX(ctx: CommandContext, steps: number, keySteps: number = steps): void {
  const store = ctx.store;
  const ids = editableNotes(ctx, true);
  if (!Array.isArray(ids)) {
    refuse(ctx, ids);
    return;
  }
  if (store === null) return;
  const dx = Math.round(xNudgeStep(ctx.editor.getState().snap) * steps * 100) / 100;
  const keys = keysOfSelection(ctx);
  const edits = keyValueEdits(store, keys, keySteps);
  const count = ids.length + new Set(edits.map((e) => e.key)).size;
  noteTx(
    ctx,
    ref(m.txNudge, { count }),
    'notes.nudge',
    (tx) => {
      Core.notes.moveNotes(tx, { ids, dt: Beats.ZERO, dx });
      for (const e of edits) Core.keys.setKeyValues(tx, { keys: [e.key], part: e.part, value: e.value });
    },
    `nudge:${selHash([...ids, ...keys])}`,
  );
}

/** Ctrl+Shift+Up / Down: selected Holds' ends by one snap step. */
export function nudgeHoldEnds(ctx: CommandContext, steps: number): void {
  const store = ctx.store;
  const ids = editableNotes(ctx);
  if (!Array.isArray(ids)) {
    refuse(ctx, ids);
    return;
  }
  if (store === null) return;
  const step = snapStepBeat(ctx.editor.getState().snap);
  const holds = ids.map((id) => store.doc.notes.get(id)).filter((n) => n?.kind === 'hold');
  if (holds.length === 0) {
    ctx.ui.toast(ref(m.reasonNoHolds), { kind: 'info' });
    return;
  }
  noteTx(
    ctx,
    ref(m.txHoldLength, { count: holds.length }),
    'notes.holdLength',
    (tx) => {
      for (const n of holds) {
        if (n === undefined) continue;
        let end = Beats.add(n.end, Beats.mulInt(step, steps));
        const min = Beats.add(n.t, step);
        if (Beats.lt(end, min)) end = min;
        if (!Beats.eq(end, n.end)) Core.notes.setNoteTimes(tx, { id: n.id, end });
      }
    },
    `holdEnd:${selHash(ids)}`,
  );
}

function holdsEnabled(ctx: CommandContext): Enabled {
  const r = editableNotes(ctx);
  if (!Array.isArray(r)) return r;
  const store = ctx.store;
  return store !== null && r.some((id) => store.doc.notes.get(id)?.kind === 'hold') ? true : ref(m.reasonNoHolds);
}

const noteCommands: readonly CommandDef[] = [
  notesCommand(
    'notes.mirror',
    m.mirror,
    'FlipHorizontal2',
    ['mirror', 'MirrorY', 'flip x', 'EXEC-01', 'NOTE-09'],
    'notes',
    10,
    (ctx, ids) => {
      const keys = keysOfSelection(ctx);
      const done = noteTx(ctx, ref(m.txMirror, { count: ids.length }), 'notes.mirror', (tx) => {
        Core.notes.mirrorNotesX(tx, { ids, around: 'zero' });
        if (keys.length > 0) Core.transform.negateKeys(tx, { keys });
      });
      if (done !== null && keys.length > 0) {
        ctx.ui.toast(
          ref(m.toastMirrored, {
            notes: itemsText(ctx, { notes: ids.length }),
            keys: itemsText(ctx, { keys: keys.length }),
          }),
          { kind: 'info' },
        );
      }
    },
    { hint: 6 },
    true,
  ),
  notesCommand(
    'notes.mirrorCenter',
    m.mirrorCenter,
    'FlipHorizontal',
    ['MirrorMid', 'mirror centre', 'EXEC-02'],
    'notes',
    20,
    (ctx, ids) => {
      noteTx(ctx, ref(m.txMirror, { count: ids.length }), 'notes.mirrorCenter', (tx) => {
        Core.notes.mirrorNotesX(tx, { ids, around: 'center' });
      });
    },
    { hint: 2 },
  ),
  {
    ...notesCommand(
      'notes.flipSide',
      m.flipSide,
      'FlipVertical2',
      ['side', 'SideSwitch', 'above below', 'EXEC-03', 'NOTE-10'],
      'notes',
      30,
      (ctx, ids) => {
        const keys = keysOfSelection(ctx);
        noteTx(ctx, ref(m.txFlip, { count: ids.length }), 'notes.flipSide', (tx) => {
          Core.notes.setSide(tx, { ids, above: 'toggle' });
          if (keys.length > 0) Core.transform.opacityFull(tx, { keys });
        });
      },
      { hint: 5 },
      true,
    ),
    enabled: (ctx) => {
      const base = notesKeysEnabled(ctx);
      if (base !== true) return base;
      const keys = keysOfSelection(ctx);
      return keys.every((k) => isOpacityKey(ctx, k)) ? true : ref(m.reasonOpacityOnly);
    },
  },
  notesCommand('notes.setTop', m.setTop, 'ArrowUpToLine', ['SideUp', 'above', 'EXEC-04'], 'notes', 40, (ctx, ids) => {
    noteTx(ctx, ref(m.txSide, { count: ids.length }), 'notes.setTop', (tx) => {
      Core.notes.setSide(tx, { ids, above: true });
    });
  }),
  notesCommand(
    'notes.setBottom',
    m.setBottom,
    'ArrowDownToLine',
    ['SideDown', 'below', 'EXEC-04'],
    'notes',
    50,
    (ctx, ids) => {
      noteTx(ctx, ref(m.txSide, { count: ids.length }), 'notes.setBottom', (tx) => {
        Core.notes.setSide(tx, { ids, above: false });
      });
    },
  ),
  notesCommand(
    'notes.toggleFake',
    m.toggleFake,
    'Ghost',
    ['fake', 'real', 'isFake', 'EXEC-05'],
    'fake',
    10,
    (ctx, ids) => {
      noteTx(ctx, ref(m.txFake, { count: ids.length }), 'notes.toggleFake', (tx) => {
        Core.notes.setFake(tx, { ids, fake: 'toggle' });
      });
    },
    { hint: 3 },
  ),
  notesCommand('notes.setFake', m.setFake, 'Ghost', ['ToFake', 'EXEC-05'], 'fake', 20, (ctx, ids) => {
    noteTx(ctx, ref(m.txFake, { count: ids.length }), 'notes.setFake', (tx) => {
      Core.notes.setFake(tx, { ids, fake: true });
    });
  }),
  notesCommand('notes.setReal', m.setReal, 'Target', ['ToReal', 'EXEC-05'], 'fake', 30, (ctx, ids) => {
    noteTx(ctx, ref(m.txFake, { count: ids.length }), 'notes.setReal', (tx) => {
      Core.notes.setFake(tx, { ids, fake: false });
    });
  }),
  convertCommand('notes.toTap', 'tap', m.toTap, 10),
  convertCommand('notes.toDrag', 'drag', m.toDrag, 20),
  convertCommand('notes.toFlick', 'flick', m.toFlick, 30),
  convertCommand('notes.toHold', 'hold', m.toHold, 40),
  notesCommand(
    'notes.snapX',
    m.snapX,
    'Magnet',
    ['AttachX', 'snap x', 'EXEC-07', 'GRID-04'],
    'snap',
    10,
    (ctx, ids) => {
      const count = Math.max(2, ctx.editor.getState().snap.xGrid);
      noteTx(ctx, ref(m.txSnapX, { count: ids.length }), 'notes.snapX', (tx) => {
        Core.notes.snapNotesX(tx, { ids, count });
      });
    },
  ),
  notesCommand('notes.quantize', m.quantize, 'Grid3x3', ['quantize', 'snap times'], 'snap', 20, (ctx, ids) => {
    const div = Math.max(1, ctx.editor.getState().snap.div);
    noteTx(ctx, ref(m.txQuantize, { count: ids.length }), 'notes.quantize', (tx) => {
      Core.notes.quantizeNotes(tx, { ids, div });
    });
  }),
  {
    id: 'notes.reverse',
    title: m.reverse,
    category: 'Notes',
    icon: 'ArrowDownUp',
    aliases: ['reverse', 'retrograde', 'time reverse'],
    level: 'full',
    enabled: reverseEnabled,
    menus: [{ menu: LANE_MENU, group: 'snap', order: 30 }],
    run: (ctx) => {
      const ok = reverseEnabled(ctx);
      if (ok !== true) {
        refuse(ctx, ok);
        return;
      }
      const sel = selectedItems(ctx);
      noteTx(ctx, ref(m.txReverse, { count: sel.notes.length + sel.segments.length }), 'notes.reverse', (tx) => {
        Core.transform.reverseTime(tx, { notes: sel.notes, segments: sel.segments });
      });
    },
  },
  {
    id: 'notes.nudgeLater',
    title: m.nudgeLater,
    category: 'Notes',
    icon: 'ChevronUp',
    aliases: ['nudge', 'move later', 'SEL-09'],
    level: 'guided',
    repeat: true,
    hint: 2,
    args: { kind: 'number', label: ref(m.argSteps), min: 1, max: 1920 },
    enabled: notesKeysEnabled,
    run: (ctx, args) => {
      nudgeTime(ctx, numArg(args, 'steps', 1));
    },
  },
  {
    id: 'notes.nudgeEarlier',
    title: m.nudgeEarlier,
    category: 'Notes',
    icon: 'ChevronDown',
    aliases: ['nudge', 'move earlier', 'SEL-09'],
    level: 'guided',
    repeat: true,
    args: { kind: 'number', label: ref(m.argSteps), min: 1, max: 1920 },
    enabled: notesKeysEnabled,
    run: (ctx, args) => {
      nudgeTime(ctx, -numArg(args, 'steps', 1));
    },
  },
  {
    id: 'notes.nudgeRight',
    title: m.nudgeRight,
    category: 'Notes',
    icon: 'ChevronRight',
    aliases: ['nudge', 'move right', 'SEL-09'],
    level: 'guided',
    repeat: true,
    hint: 1,
    args: { kind: 'number', label: ref(m.argSteps), min: 1, max: 1000 },
    enabled: notesKeysEnabled,
    run: (ctx, args) => {
      const n = numArg(args, 'steps', 1);
      nudgeX(ctx, n, numArg(args, 'keySteps', n));
    },
  },
  {
    id: 'notes.nudgeLeft',
    title: m.nudgeLeft,
    category: 'Notes',
    icon: 'ChevronLeft',
    aliases: ['nudge', 'move left', 'SEL-09'],
    level: 'guided',
    repeat: true,
    args: { kind: 'number', label: ref(m.argSteps), min: 1, max: 1000 },
    enabled: notesKeysEnabled,
    run: (ctx, args) => {
      const n = numArg(args, 'steps', 1);
      nudgeX(ctx, -n, -numArg(args, 'keySteps', n));
    },
  },
  {
    id: 'notes.wheelWidth',
    title: m.wheelWidth,
    category: 'Notes',
    icon: 'MoveHorizontal',
    aliases: ['width', 'size', 'wider', 'narrower', 'Ctrl wheel', 'NOTE-13'],
    level: 'full',
    repeat: true,
    args: { kind: 'number', label: ref(m.argWidthSteps), min: -100, max: 100 },
    enabled: notesEnabled,
    run: (ctx, args) => {
      const ids = editableNotes(ctx);
      if (!Array.isArray(ids)) {
        refuse(ctx, ids);
        return;
      }
      fineTuneWidth(ctx, stepsOf(args), ids);
    },
  },
  {
    id: 'notes.holdLonger',
    title: m.holdLonger,
    category: 'Notes',
    icon: 'ChevronsUp',
    aliases: ['hold end', 'longer'],
    level: 'guided',
    repeat: true,
    enabled: holdsEnabled,
    run: (ctx) => {
      nudgeHoldEnds(ctx, 1);
    },
  },
  {
    id: 'notes.holdShorter',
    title: m.holdShorter,
    category: 'Notes',
    icon: 'ChevronsDown',
    aliases: ['hold end', 'shorter'],
    level: 'guided',
    repeat: true,
    enabled: holdsEnabled,
    run: (ctx) => {
      nudgeHoldEnds(ctx, -1);
    },
  },
  {
    id: 'notes.transform',
    title: m.transformDialog,
    category: 'Notes',
    icon: 'Scaling',
    aliases: ['transform', 'scale time', 'stretch', 'offset'],
    level: 'guided',
    enabled: (ctx) => {
      if (ctx.store === null) return ref(m.reasonNoProject);
      const sel = selectedItems(ctx);
      if (sel.notes.length + sel.keys.length + sel.segments.length === 0) return ref(m.reasonNoSelection);
      return lockedReason(ctx, { notes: sel.notes, keys: keysOfSelection(ctx) }) ?? true;
    },
    menus: [{ menu: LANE_MENU, group: 'transform', order: 10 }],
    run: async (ctx) => {
      const sel = selectedItems(ctx);
      const locked = lockedReason(ctx, { notes: sel.notes, keys: keysOfSelection(ctx) });
      if (locked !== null) {
        refuse(ctx, locked);
        return;
      }
      const pivot = snapBeatAt(playheadBeat(ctx), ctx.editor.getState().snap, false);
      await ctx.ui.openDialog(DIALOG.transform, { notes: sel.notes, keys: keysOfSelection(ctx), pivotBeat: pivot });
    },
  },
];

// ---------------------------------------------------------------------------------------------------------------
// Edit: clipboard, delete, select all
// ---------------------------------------------------------------------------------------------------------------

function anySelected(ctx: CommandContext): Enabled {
  if (ctx.store === null) return ref(m.reasonNoProject);
  const sel = selectedItems(ctx);
  return sel.notes.length + sel.keys.length + sel.segments.length > 0 ? true : ref(m.reasonNoSelection);
}

/** Notes, keys and segments of the selection on a locked line: the reason, else null. */
function selectionLocked(ctx: CommandContext): MessageRef | null {
  const sel = selectedItems(ctx);
  return lockedReason(ctx, { notes: sel.notes, keys: [...sel.keys, ...sel.segments] });
}

/** Cut and duplicate change the selection's lines (cut deletes there, duplicate pastes onto the same lines). */
function anySelectedEditable(ctx: CommandContext): Enabled {
  const base = anySelected(ctx);
  if (base !== true) return base;
  return selectionLocked(ctx) ?? true;
}

function reverseEnabled(ctx: CommandContext): Enabled {
  if (ctx.store === null) return ref(m.reasonNoProject);
  const sel = selectedItems(ctx);
  if (sel.notes.length + sel.segments.length === 0) return ref(m.reasonNoSelection);
  return lockedReason(ctx, { notes: sel.notes, keys: sel.segments }) ?? true;
}

/**
 * Delete / Backspace: notes, keys, segments and markers, one step. A selected segment (not also selected as a key)
 * loses its motion as the motion columns' "Delete segments" does (UX-037): a middle segment becomes a Stay, a last
 * segment loses its end key (core/commands/segmentDelete.ts).
 */
export function deleteSelection(ctx: CommandContext): void {
  const store = ctx.store;
  if (store === null) return;
  const sel = selectedItems(ctx);
  const keySet = new Set<KeyId>(sel.keys);
  const segments = sel.segments.filter((id) => !keySet.has(id));
  const plan = planSegmentDelete(store, segments);
  const segmentCount = plan.stay.length + plan.trimEnds.length;
  const locked = lockedReason(ctx, { notes: sel.notes, keys: [...sel.keys, ...segments] });
  if (locked !== null) {
    refuse(ctx, locked);
    return;
  }
  const items = itemsText(ctx, {
    notes: sel.notes.length,
    keys: sel.keys.length,
    segments: segmentCount,
    markers: sel.markers.length,
  });
  const done = noteTx(ctx, ref(m.txDelete, { items }), 'edit.delete', (tx) => {
    if (sel.notes.length > 0) Core.notes.deleteNotes(tx, { ids: sel.notes });
    if (segmentCount > 0) deleteSegments(tx, { segments });
    const keys = sel.keys.filter((id) => tx.doc.keys.has(id));
    if (keys.length > 0) Core.keys.deleteKeys(tx, { keys });
    for (const id of sel.markers) Core.tempo.deleteMarker(tx, { id });
    tx.setSelection({ notes: [], keys: [], segments: [], markers: [] });
    return true;
  });
  if (done !== null) ctx.ui.toast(ref(m.toastDeleted, { items }), { kind: 'info', undo: true, durationMs: 3000 });
}

function sameIds(a: ReadonlySet<number>, b: readonly number[]): boolean {
  if (a.size !== b.length) return false;
  for (const x of b) if (!a.has(x)) return false;
  return true;
}

/** Ctrl+A: everything in the focused column (motion) or the active lane; again: the whole line. */
export function selectAll(ctx: CommandContext): void {
  const store = ctx.store;
  if (store === null) return;
  const e = ctx.editor.getState();
  const sel = e.selection;
  const focusMotion = ctx.when.has('focus.motion') || ctx.when.has('focus.curve');
  const fc = e.focusedColumn;
  const wholeLine = (line: LineId): void => {
    const keys: KeyId[] = [];
    for (const tracks of store.idx.tracksOfLine(line).values())
      for (const t of tracks) keys.push(...store.idx.keysByTrack(t));
    e.select({ notes: new Set(store.idx.notesByLine(line)), keys: new Set(keys) }, 'replace');
  };
  if (focusMotion && fc !== null && store.doc.lines.has(fc.line)) {
    const track = store.idx.track(fc.line, fc.prop, fc.layer);
    const keys = track === undefined ? [] : [...store.idx.keysByTrack(track)];
    if (sel.notes.size === 0 && sameIds(sel.keys, keys) && keys.length > 0) wholeLine(fc.line);
    else e.select({ keys: new Set(keys) }, 'replace');
    return;
  }
  const line = activeLineOf(ctx);
  if (line === null) {
    ctx.ui.toast(ref(m.reasonNoLine), { kind: 'info' });
    return;
  }
  const s = laneSession(ctx);
  const index = noteIndexOf(store, line);
  const notes: NoteId[] = [];
  for (let i = 0; i < index.count; i++) {
    const below = ((index.flags[i] ?? 0) & 2) !== 0;
    if (s.sides === 'both' || (s.sides === 'below') === below) notes.push((index.ids[i] ?? -1) as NoteId);
  }
  if (sel.keys.size === 0 && sameIds(sel.notes, notes) && notes.length > 0) wholeLine(line);
  else e.select({ notes: new Set(notes) }, 'replace');
}

const PASTE_CHOICES = [
  { value: 'normal', label: ref(m.pasteNormal) },
  { value: 'mirror', label: ref(m.pasteMirrored) },
  { value: 'addValues', label: ref(m.pasteAddValues) },
  { value: 'special', label: ref(m.pasteSpecialChoice) },
] as const;

const editCommands: readonly CommandDef[] = [
  {
    id: 'edit.copy',
    title: m.copy,
    category: 'Edit',
    icon: 'Copy',
    aliases: ['copy', 'SEL-04'],
    level: 'guided',
    hint: 3,
    enabled: anySelected,
    menus: [
      { menu: 'app.edit', group: 'clipboard', order: 20 },
      { menu: LANE_MENU, group: 'clipboard', order: 20 },
    ],
    run: async (ctx) => {
      await copySelection(ctx, false);
    },
  },
  {
    id: 'edit.cut',
    title: m.cut,
    category: 'Edit',
    icon: 'Scissors',
    aliases: ['cut', 'SEL-04'],
    level: 'guided',
    enabled: anySelectedEditable,
    menus: [
      { menu: 'app.edit', group: 'clipboard', order: 10 },
      { menu: LANE_MENU, group: 'clipboard', order: 10 },
    ],
    run: async (ctx) => {
      await copySelection(ctx, true);
    },
  },
  {
    id: 'edit.paste',
    title: m.paste,
    category: 'Edit',
    icon: 'ClipboardPaste',
    aliases: ['paste', 'mirror paste', 'numeric paste', 'add values', 'SEL-05', 'SEL-06', 'SEL-07'],
    level: 'guided',
    hint: 2,
    args: { kind: 'choice', label: ref(m.argPasteVariant), options: PASTE_CHOICES },
    enabled: projectOpen,
    menus: [
      { menu: 'app.edit', group: 'clipboard', order: 30 },
      { menu: LANE_MENU, group: 'clipboard', order: 30 },
    ],
    run: async (ctx, args) => {
      const a = parsePasteArgs(args);
      switch (a.kind) {
        case 'rest':
          pasteRest(ctx, a.token);
          return;
        case 'specialDialog':
          await pasteSpecial(ctx);
          return;
        case 'special':
          await startPaste(ctx, { variant: 'normal', special: a.result, command: 'edit.paste' });
          return;
        case 'paste': {
          // From the lane's context menu: at the right-clicked beat.
          const hit = ctx.source === 'context' ? laneSession(ctx).context : null;
          const at = hit === null ? undefined : snapBeatAt(hit.beat, ctx.editor.getState().snap, false);
          await startPaste(ctx, { variant: a.variant, command: 'edit.paste', ...(at === undefined ? {} : { at }) });
          return;
        }
      }
    },
  },
  {
    id: 'edit.pasteGhost',
    title: m.pasteGhost,
    category: 'Edit',
    icon: 'ClipboardPaste',
    aliases: ['paste ghost', 'paste preview'],
    level: 'guided',
    enabled: projectOpen,
    run: async (ctx) => {
      await startPaste(ctx, { variant: 'normal', force: 'ghost', command: 'edit.paste' });
    },
  },
  {
    id: 'edit.pasteImmediate',
    title: m.pasteImmediate,
    category: 'Edit',
    icon: 'ClipboardCheck',
    aliases: ['paste now', 'paste at mouse'],
    level: 'guided',
    enabled: projectOpen,
    run: async (ctx) => {
      await startPaste(ctx, { variant: 'normal', force: 'immediate', command: 'edit.paste' });
    },
  },
  {
    id: 'edit.duplicate',
    title: m.duplicate,
    category: 'Edit',
    icon: 'CopyPlus',
    aliases: ['duplicate', 'repeat after'],
    level: 'guided',
    hint: 1,
    enabled: anySelectedEditable,
    menus: [
      { menu: 'app.edit', group: 'clipboard', order: 40 },
      { menu: LANE_MENU, group: 'clipboard', order: 40 },
    ],
    run: (ctx) => {
      duplicateSelection(ctx);
    },
  },
  {
    id: 'edit.delete',
    title: m.delete,
    category: 'Edit',
    icon: 'Trash2',
    aliases: ['delete', 'remove', 'NOTE-07', 'SEL-08'],
    level: 'guided',
    hint: 4,
    enabled: (ctx) => {
      if (ctx.store === null) return ref(m.reasonNoProject);
      if (ctx.store.gestureActive) return ref(m.reasonGesture);
      const sel = selectedItems(ctx);
      if (sel.notes.length + sel.keys.length + sel.segments.length + sel.markers.length === 0)
        return ref(m.reasonNoSelection);
      return selectionLocked(ctx) ?? true;
    },
    menus: [
      { menu: 'app.edit', group: 'delete', order: 10 },
      { menu: LANE_MENU, group: 'delete', order: 10 },
    ],
    run: (ctx) => {
      deleteSelection(ctx);
    },
  },
  {
    id: 'edit.selectAll',
    title: m.selectAll,
    category: 'Select',
    icon: 'BoxSelect',
    aliases: ['select all', 'all notes'],
    level: 'guided',
    hint: 1,
    enabled: projectOpen,
    menus: [
      { menu: 'app.edit', group: 'select', order: 10 },
      { menu: LANE_MENU, group: 'select', order: 10 },
    ],
    run: (ctx) => {
      selectAll(ctx);
    },
  },
];

// ---------------------------------------------------------------------------------------------------------------
// Paste ghost
// ---------------------------------------------------------------------------------------------------------------

function ghostUp(ctx: CommandContext): Enabled {
  return ctx.editor.getState().placement?.kind === 'paste' ? true : ref(m.reasonNoPaste);
}

function ghostCommand(
  id: string,
  title: MessageKey,
  icon: string,
  run: (ctx: CommandContext) => void,
  hint?: number,
): CommandDef {
  return {
    id,
    title,
    category: 'Edit',
    icon,
    level: 'guided',
    enabled: ghostUp,
    ...(hint === undefined ? {} : { hint }),
    run,
  };
}

const ghostCommands: readonly CommandDef[] = [
  ghostCommand('lane.pasteMirror', m.pasteMirrorToggle, 'FlipHorizontal2', (ctx) => {
    toggleGhostMirror(ctx);
  }),
  ghostCommand('lane.pasteNextLine', m.pasteNextLine, 'ArrowRightLeft', (ctx) => {
    cycleGhostLine(ctx, 1);
  }),
  ghostCommand('lane.pastePrevLine', m.pastePrevLine, 'ArrowRightLeft', (ctx) => {
    cycleGhostLine(ctx, -1);
  }),
  ghostCommand('lane.pasteLater', m.pasteLater, 'ChevronUp', (ctx) => {
    shiftGhost(ctx, 1);
  }),
  ghostCommand('lane.pasteEarlier', m.pasteEarlier, 'ChevronDown', (ctx) => {
    shiftGhost(ctx, -1);
  }),
  ghostCommand(
    'lane.pasteCommit',
    m.pasteCommit,
    'Check',
    (ctx) => {
      commitGhost(ctx);
    },
    10,
  ),
  ghostCommand('lane.pasteCancel', m.pasteCancel, 'X', (ctx) => {
    cancelGhost(ctx);
  }),
];

// ---------------------------------------------------------------------------------------------------------------
// Lanes: pinned lanes, also show, X view, sides, RPE hold aliases
// ---------------------------------------------------------------------------------------------------------------

function relayout(ctx: CommandContext): void {
  hostOf(laneSession(ctx))?.relayout();
}

const SIDES_CHOICES = [
  { value: 'both', label: ref(m.sidesBoth) },
  { value: 'above', label: ref(m.sidesAbove) },
  { value: 'below', label: ref(m.sidesBelow) },
] as const;

function zoomLaneX(ctx: CommandContext, factor: number): void {
  const tl = ctx.editor.getState().timeline;
  const v = clampXView({ center: tl.laneCenterX, half: tl.laneHalfRange / factor });
  ctx.editor.getState().setTimeline({ laneCenterX: v.center, laneHalfRange: v.half });
}

/** X extent of the active line's notes (null without notes). */
function notesXExtent(ctx: CommandContext): readonly [number, number] | null {
  const index = noteIndexOf(ctx.store, activeLineOf(ctx));
  if (index.count === 0) return null;
  let lo = Number.POSITIVE_INFINITY;
  let hi = Number.NEGATIVE_INFINITY;
  for (let i = 0; i < index.count; i++) {
    const x = index.x[i] ?? 0;
    lo = Math.min(lo, x);
    hi = Math.max(hi, x);
  }
  return [lo, hi];
}

const laneCommands: readonly CommandDef[] = [
  {
    id: 'lane.pin',
    title: m.pinLane,
    category: 'View',
    icon: 'Pin',
    aliases: ['pin lane', 'extra lane', 'multi line', 'VIEW-06'],
    level: 'full',
    args: { kind: 'line', label: ref(m.argLine) },
    enabled: projectOpen,
    run: (ctx, args) => {
      const line = lineArg(ctx, args) ?? activeLineOf(ctx);
      if (line === null) return;
      const e = ctx.editor.getState();
      if (!e.pinnedLanes.includes(line)) e.setPinnedLanes([...e.pinnedLanes, line]);
      relayout(ctx);
    },
  },
  {
    id: 'lane.unpin',
    title: m.unpinLane,
    category: 'View',
    icon: 'PinOff',
    level: 'full',
    args: { kind: 'line', label: ref(m.argLine) },
    enabled: (ctx) => (ctx.editor.getState().pinnedLanes.length > 0 ? true : ref(m.reasonNoLine)),
    run: (ctx, args) => {
      const e = ctx.editor.getState();
      const line = lineArg(ctx, args) ?? e.pinnedLanes[e.pinnedLanes.length - 1] ?? null;
      if (line === null) return;
      e.setPinnedLanes(e.pinnedLanes.filter((l) => l !== line));
      relayout(ctx);
    },
  },
  {
    id: 'lane.alsoShow',
    title: m.alsoShow,
    category: 'View',
    icon: 'Layers',
    aliases: ['also show', 'ghost notes', 'other lines', 'VIEW-06'],
    level: 'full',
    args: { kind: 'line', label: ref(m.argLine) },
    enabled: projectOpen,
    run: (ctx, args) => {
      const line = lineArg(ctx, args);
      if (line === null) return;
      const e = ctx.editor.getState();
      if (!e.alsoShow.includes(line) && line !== e.activeLine) e.setAlsoShow([...e.alsoShow, line]);
    },
  },
  {
    id: 'lane.alsoHide',
    title: m.alsoHide,
    category: 'View',
    icon: 'EyeOff',
    level: 'full',
    args: { kind: 'line', label: ref(m.argLine) },
    enabled: (ctx) => (ctx.editor.getState().alsoShow.length > 0 ? true : ref(m.reasonNoLine)),
    run: (ctx, args) => {
      const e = ctx.editor.getState();
      const line = lineArg(ctx, args);
      if (line !== null) e.setAlsoShow(e.alsoShow.filter((l) => l !== line));
    },
  },
  {
    id: 'lane.alsoClear',
    title: m.alsoClear,
    category: 'View',
    icon: 'EyeOff',
    level: 'full',
    enabled: (ctx) => (ctx.editor.getState().alsoShow.length > 0 ? true : ref(m.reasonNoLine)),
    run: (ctx) => {
      ctx.editor.getState().setAlsoShow([]);
    },
  },
  {
    id: 'lane.zoomXIn',
    title: m.zoomXIn,
    category: 'View',
    icon: 'ZoomIn',
    aliases: ['lane zoom', 'vision zoom', 'VIEW-04'],
    level: 'guided',
    enabled: projectOpen,
    run: (ctx) => {
      zoomLaneX(ctx, 1.25);
    },
  },
  {
    id: 'lane.zoomXOut',
    title: m.zoomXOut,
    category: 'View',
    icon: 'ZoomOut',
    aliases: ['lane zoom', 'vision zoom', 'VIEW-04'],
    level: 'guided',
    enabled: projectOpen,
    run: (ctx) => {
      zoomLaneX(ctx, 1 / 1.25);
    },
  },
  {
    id: 'lane.fitX',
    title: m.fitX,
    category: 'View',
    icon: 'Crosshair',
    aliases: ['fit lane', 'show all x', 'VIEW-05'],
    level: 'guided',
    enabled: projectOpen,
    run: (ctx) => {
      const ext = notesXExtent(ctx);
      const v = ext === null ? fitXView(0, 0) : fitXView(ext[0], ext[1]);
      ctx.editor.getState().setTimeline({ laneCenterX: v.center, laneHalfRange: v.half });
    },
  },
  {
    id: 'lane.sides',
    title: m.sides,
    category: 'View',
    icon: 'ArrowUpDown',
    aliases: ['above', 'below', 'side filter'],
    level: 'full',
    args: { kind: 'choice', label: ref(m.argSides), options: SIDES_CHOICES },
    run: (ctx, args) => {
      const v = typeof args === 'string' ? args : obj(args).sides;
      const sides: LaneSides = v === 'above' || v === 'below' ? v : 'both';
      setSides(laneSession(ctx), sides);
    },
  },
  {
    id: 'lane.holdMove',
    title: m.holdMoveAlias,
    category: 'Notes',
    icon: 'Move',
    aliases: ['Z drag', 'NOTE-11', 'NOTE-12'],
    level: 'full',
    kind: 'hold',
    run: (ctx) => {
      laneSession(ctx).heldZ = true;
    },
    release: (ctx) => {
      laneSession(ctx).heldZ = false;
    },
  },
  {
    id: 'lane.holdDelete',
    title: m.holdDeleteAlias,
    category: 'Notes',
    icon: 'Trash2',
    aliases: ['D right click', 'NOTE-07'],
    level: 'full',
    kind: 'hold',
    run: (ctx) => {
      laneSession(ctx).heldD = true;
    },
    release: (ctx) => {
      laneSession(ctx).heldD = false;
    },
  },
];

/** Every command of this file. */
export const LANE_EDIT_COMMANDS: readonly CommandDef[] = [
  ...toolCommands,
  ...placementCommands,
  ...noteCommands,
  ...editCommands,
  ...ghostCommands,
  ...laneCommands,
];

// ---------------------------------------------------------------------------------------------------------------
// Key bindings (DESIGN.md §8.2 Default, §8.3 RPE)
// ---------------------------------------------------------------------------------------------------------------

const steps = (n: number): Partial<KeyBinding> => ({ args: { steps: n } satisfies Json, when: NOTES_WHEN });
/** Left / Right: notes by half X-grid steps (Shift x4), selected keys by value steps (Shift x10, as keys alone). */
const xSteps = (n: number, keys: number): Partial<KeyBinding> => ({
  args: { steps: n, keySteps: keys } satisfies Json,
  when: NOTES_WHEN,
});
const GHOST = 'paste.ghost';

const SHARED: readonly KeyBinding[] = [
  kb('KeyV', 'tools.select'),
  kb('Digit1', 'tools.tap'),
  kb('Digit2', 'tools.drag'),
  kb('Digit3', 'tools.flick'),
  kb('Digit4', 'tools.hold'),
  kb('KeyX', 'tools.eraser'),
  kb('KeyQ', 'notes.placeTap'),
  kb('KeyW', 'notes.placeDrag'),
  kb('KeyE', 'notes.placeFlick'),
  kb('KeyR', 'notes.hold'),
  kb('Shift+KeyQ', 'notes.placeTapAtPlayhead'),
  kb('Shift+KeyW', 'notes.placeDragAtPlayhead'),
  kb('Shift+KeyE', 'notes.placeFlickAtPlayhead'),
  kb('Shift+KeyR', 'notes.holdAtPlayhead'),
  kb('Escape', 'notes.cancelHold', { when: 'placing.hold && !dialog' }),
  kb('KeyA', 'notes.mirror', { when: NOTES_WHEN }),
  kb('Shift+KeyA', 'notes.mirrorCenter', { when: NOTES_WHEN }),
  kb('KeyS', 'notes.flipSide', { when: NOTES_WHEN }),
  kb('Shift+KeyF', 'notes.toggleFake', { when: NOTES_WHEN }),
  kb('Alt+KeyQ', 'notes.toTap', { when: NOTES_WHEN, altFree: null }),
  kb('Alt+KeyW', 'notes.toDrag', { when: NOTES_WHEN, altFree: null }),
  kb('Alt+KeyE', 'notes.toFlick', { when: NOTES_WHEN, altFree: null }),
  kb('Alt+KeyR', 'notes.toHold', { when: NOTES_WHEN, altFree: null }),
  kb('Alt+KeyX', 'notes.snapX', { when: NOTES_WHEN, altFree: null }),
  kb('ArrowUp', 'notes.nudgeLater', steps(1)),
  kb('ArrowDown', 'notes.nudgeEarlier', steps(1)),
  kb('Shift+ArrowUp', 'notes.nudgeLater', steps(4)),
  kb('Shift+ArrowDown', 'notes.nudgeEarlier', steps(4)),
  kb('ArrowRight', 'notes.nudgeRight', xSteps(1, 1)),
  kb('ArrowLeft', 'notes.nudgeLeft', xSteps(1, 1)),
  kb('Shift+ArrowRight', 'notes.nudgeRight', xSteps(4, 10)),
  kb('Shift+ArrowLeft', 'notes.nudgeLeft', xSteps(4, 10)),
  kb('Ctrl+Shift+ArrowUp', 'notes.holdLonger', { when: NOTES_WHEN }),
  kb('Ctrl+Shift+ArrowDown', 'notes.holdShorter', { when: NOTES_WHEN }),
  kb('Ctrl+KeyC', 'edit.copy'),
  kb('Ctrl+KeyX', 'edit.cut'),
  kb('Ctrl+KeyV', 'edit.paste'),
  kb('Ctrl+KeyB', 'edit.paste', { args: { variant: 'mirror' } }),
  kb('Alt+KeyV', 'edit.paste', { args: { variant: 'addValues' }, altFree: null }),
  kb('Ctrl+KeyD', 'edit.duplicate'),
  // Notes (and mixed selections); keys alone are the motion columns' (`sel.keys && !sel.notes`); markers alone here.
  kb('Delete', 'edit.delete', { when: NOTES_WHEN }),
  kb('Backspace', 'edit.delete', { when: NOTES_WHEN }),
  kb('Delete', 'edit.delete', { when: MARKERS_WHEN }),
  kb('Backspace', 'edit.delete', { when: MARKERS_WHEN }),
  kb('Ctrl+KeyA', 'edit.selectAll'),
  kb('KeyM', 'lane.pasteMirror', { when: GHOST }),
  kb('Tab', 'lane.pasteNextLine', { when: GHOST }),
  kb('Shift+Tab', 'lane.pastePrevLine', { when: GHOST }),
  kb('BracketRight', 'lane.pasteLater', { when: GHOST }),
  kb('BracketLeft', 'lane.pasteEarlier', { when: GHOST }),
  kb('Enter', 'lane.pasteCommit', { when: GHOST }),
  kb('NumpadEnter', 'lane.pasteCommit', { when: GHOST }),
  kb('Escape', 'lane.pasteCancel', { when: `${GHOST} && !dialog` }),
];

export const LANE_EDIT_KEYBINDINGS: Readonly<Record<KeymapPresetId, readonly KeyBinding[]>> = {
  default: SHARED,
  rpe: [
    ...SHARED,
    // NOTE-11/12: Z + drag moves notes (Z + right-drag a Hold's end); NOTE-07: D + right-click deletes.
    kb('hold:KeyZ', 'lane.holdMove'),
    kb('hold:KeyD', 'lane.holdDelete'),
  ],
};

/** The instance id of the lane showing `line` (active lane or a pinned one). */
export function laneIdForLine(ctx: Pick<AppServices, 'editor'>, line: LineId): string | null {
  const e = ctx.editor.getState();
  if (e.activeLine === line) return ACTIVE_LANE_ID;
  return e.pinnedLanes.includes(line) ? `lane:${String(line)}` : null;
}

export { lineOfLaneId };
